import type { components } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

export type TeamFixturesResponse =
  components['schemas']['TeamFixturesResponse'];
export type TeamWithFixtures = TeamFixturesResponse['data'][number];
export type TeamFixture = TeamWithFixtures['fixtures'][number];
export type TeamFixtureInferences = NonNullable<TeamFixture['inferences']>;
export type TeamStrength = NonNullable<TeamWithFixtures['strength']>;
export type TeamAggregates = NonNullable<TeamWithFixtures['aggregates']>;

export type TeamFixturesSortBy =
  | 'difficulty.overall'
  | 'difficulty.attack'
  | 'difficulty.defence'
  | 'goalsFor'
  | 'cleanSheet';

export type TeamFixturesSortDirection = 'asc' | 'desc';

export type TeamFixturesParameters = {
  gameweekStart: number;
  gameweekEnd: number;
  // Which season to fetch. The endpoint defaults to the season of the current
  // gameweek, but in the off-season there is no current gameweek, so callers
  // must name the upcoming season explicitly to surface its fixtures.
  season?: number;
  excludeGameweeks?: number[];
  sortBy?: TeamFixturesSortBy;
  sortDirection?: TeamFixturesSortDirection;
  // The full fixture ticker is a paying-member feature. The token is forwarded
  // so the endpoint can authenticate the caller once it moves behind auth; the
  // public mini ticker uses fetchTeamFixturesPreview instead.
  accessToken?: string;
};

export const fetchTeamFixtures = async (
  parameters: TeamFixturesParameters
): Promise<TeamWithFixtures[]> => {
  const { accessToken, excludeGameweeks, ...rest } = parameters;

  const { data, error } = await ffhClient().GET('/league/teams/fixtures', {
    params: {
      query: {
        ...rest,
        ...(excludeGameweeks && excludeGameweeks.length > 0
          ? { excludeGameweeks: excludeGameweeks.join(',') }
          : {})
      }
    },
    ...(accessToken ? { headers: authHeader(accessToken) } : {})
  });

  if (error) throw new Error('Failed to load team fixtures');
  return data?.data ?? [];
};
